/**
 * Role-based access control (RBAC) — shared, pure definitions.
 *
 * Roles are stored as a Firebase Auth custom claim (`role`) and mirrored to a
 * Firestore `users/{uid}` document. This module is the single source of truth
 * for role identity, ordering, and capability mapping on the client. It is pure
 * (no React, no Firebase) so it can be unit-tested in isolation.
 */

export const ROLES = {
  OWNER: 'owner',
  ADMINISTRATOR: 'administrator',
  MANAGER: 'manager',
  EDITOR: 'editor',
  SERVICE_PREP: 'servicePrep',
  USER: 'user',
} as const;

export type TRole = (typeof ROLES)[keyof typeof ROLES];

/**
 * Ordered from least to most privileged. The array index is the role's rank,
 * used by {@link rankOf} and {@link isAtLeast} for hierarchy comparisons.
 */
export const ROLE_HIERARCHY: TRole[] = [
  ROLES.USER,
  ROLES.SERVICE_PREP,
  ROLES.EDITOR,
  ROLES.MANAGER,
  ROLES.ADMINISTRATOR,
  ROLES.OWNER,
];

/** Role assigned to any authenticated user without an explicit role claim. */
export const DEFAULT_ROLE: TRole = ROLES.USER;

export type TCapability =
  | 'viewSettings'
  | 'viewUserManagement'
  | 'manageUsers'
  | 'triggerSync'
  | 'planServices'
  /**
   * Write access to the people roster beyond a quick add: contact details,
   * title, notes, service-role tags on somebody else's record, and archiving.
   *
   * Reading the roster and quick-adding a name are BOTH covered by
   * planServices instead. A planner who cannot add a name is hard blocked the
   * first time a guest preaches, because picking is the only way to fill a
   * person field, since picking is the only way in.
   */
  | 'managePeople';

const CAPABILITY_MAP: Record<TRole, TCapability[]> = {
  [ROLES.OWNER]: [
    'viewSettings',
    'viewUserManagement',
    'manageUsers',
    'triggerSync',
    'planServices',
    'managePeople',
  ],
  [ROLES.ADMINISTRATOR]: [
    'viewSettings',
    'viewUserManagement',
    'manageUsers',
    'triggerSync',
    'planServices',
    'managePeople',
  ],
  [ROLES.MANAGER]: [
    'viewSettings',
    'viewUserManagement',
    'triggerSync',
    'planServices',
    'managePeople',
  ],
  [ROLES.EDITOR]: ['viewSettings', 'viewUserManagement', 'planServices', 'managePeople'],
  [ROLES.SERVICE_PREP]: ['planServices'],
  [ROLES.USER]: [],
};

/** Type guard: is the given value one of the known roles? */
export function isRole(value: unknown): value is TRole {
  return typeof value === 'string' && (ROLE_HIERARCHY as string[]).includes(value);
}

/**
 * Coerce a raw claim value into a known role. Unknown/undefined/garbage values
 * (e.g. a legacy user with no `role` claim) fall back to {@link DEFAULT_ROLE}.
 * This is the single coercion point for the raw token claim.
 */
export function normalizeRole(value: unknown): TRole {
  return isRole(value) ? value : DEFAULT_ROLE;
}

/** Numeric rank of a role (higher = more privileged). */
export function rankOf(role: TRole): number {
  return ROLE_HIERARCHY.indexOf(role);
}

/** Does `role` rank at least as high as `min`? */
export function isAtLeast(role: TRole, min: TRole): boolean {
  return rankOf(role) >= rankOf(min);
}

/** Does `role` hold the given capability? */
export function can(role: TRole, capability: TCapability): boolean {
  return CAPABILITY_MAP[role].includes(capability);
}

const ROLE_LABELS: Record<TRole, string> = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.ADMINISTRATOR]: 'Administrator',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.EDITOR]: 'Editor',
  [ROLES.SERVICE_PREP]: 'Service Prep',
  [ROLES.USER]: 'User',
};

/** Human-readable label for a role (e.g. `servicePrep` → `Service Prep`). */
export function roleLabel(role: TRole): string {
  return ROLE_LABELS[role];
}
